/**
 * /mail/api-keys — Resend-style API key management (OCP-170).
 *
 * UX critical path:
 *   1. Empty state (no keys yet) shows a code snippet + "Create your
 *      first key" prominent CTA. Solo dev who just signed up sees
 *      exactly the friction Resend's onboarding has — no friction.
 *   2. Create-key flow → reveal-once modal. Plaintext is shown ONCE
 *      with a copy button. After dismiss, only the prefix is
 *      retrievable. Mirrors Stripe/Resend/Postmark security UX.
 *   3. Per-row revoke (with confirmation) + rotate (with reveal-once).
 *
 * The plaintext key is held only in React state until the modal
 * closes. No localStorage, no analytics, no anything else touches it.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { useTransientState } from '../lib/useTransientState';
import { fmtDate } from '../lib/format-time';

interface ApiKeySummary {
  id: string;
  name: string | null;
  prefix: string;
  created_at: string | null;
  last_used_at: string | null;
}

interface ApiKeyMinted extends ApiKeySummary {
  key: string;
}

interface ListResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  api_keys: ApiKeySummary[];
}

type Modal =
  | { kind: 'create' }
  | { kind: 'reveal'; minted: ApiKeyMinted; rotated: boolean }
  | { kind: 'confirm-revoke'; key: ApiKeySummary }
  | null;

export function MailApiKeysPage() {
  const { t } = useTranslation('mail-api-keys');
  const [data, setData] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  // OCP-545 - minting or revoking a Mail API key is owner/admin only.
  const { canWrite } = useMailWriteAccess();

  const load = useCallback(async (signal?: AbortSignal) => {
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<ListResponse>('/v1/portal/mail/api-keys', { signal });
      setData(r);
      setState('idle');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError) {
        setErrorDetail(
          err.status === 502 ? t('error.unreachable') : t('error.loadHttp', { status: err.status }),
        );
      } else {
        setErrorDetail(t('error.load'));
      }
      setState('error');
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  async function createKey(name: string): Promise<void> {
    try {
      const minted = await api<ApiKeyMinted>('/v1/portal/mail/api-keys', {
        method: 'POST',
        body: name ? { name } : {},
      });
      setModal({ kind: 'reveal', minted, rotated: false });
      void load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setErrorDetail(t('error.limit'));
      } else if (err instanceof ApiError) {
        setErrorDetail(t('error.createHttp', { status: err.status }));
      } else {
        setErrorDetail(t('error.create'));
      }
      setModal(null);
    }
  }

  async function rotateKey(keyId: string): Promise<void> {
    try {
      const minted = await api<ApiKeyMinted>(
        `/v1/portal/mail/api-keys/${keyId}/rotate`,
        { method: 'POST' },
      );
      setModal({ kind: 'reveal', minted, rotated: true });
      void load();
    } catch (err) {
      setErrorDetail(
        err instanceof ApiError ? t('error.rotateHttp', { status: err.status }) : t('error.rotate'),
      );
    }
  }

  async function revokeKey(keyId: string): Promise<void> {
    try {
      await api(`/v1/portal/mail/api-keys/${keyId}`, { method: 'DELETE' });
      setModal(null);
      setData((prev) =>
        prev
          ? { ...prev, api_keys: prev.api_keys.filter((k) => k.id !== keyId) }
          : prev,
      );
    } catch (err) {
      setErrorDetail(
        err instanceof ApiError ? t('error.revokeHttp', { status: err.status }) : t('error.revoke'),
      );
    }
  }

  if (data && !data.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.text')}</p>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('header.intro')}{' '}
            <Link to="/mail/connections" className="text-brand-500 hover:underline">
              {t('header.oauthLink')}
            </Link>
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setModal({ kind: 'create' })}
            className="shrink-0 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t('header.create')}
          </button>
        )}
      </header>

      <MailReadOnlyNotice />

      {errorDetail && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      {state === 'loading' && (
        <p className="text-sm text-gray-500">{t('loading')}</p>
      )}

      {state === 'idle' && data && data.api_keys.length === 0 && (
        <EmptyState />
      )}

      {state === 'idle' && data && data.api_keys.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('table.name')}</th>
                <th className="px-3 py-2">{t('table.prefix')}</th>
                <th className="px-3 py-2">{t('table.created')}</th>
                <th className="px-3 py-2">{t('table.lastUsed')}</th>
                <th className="px-3 py-2 text-right">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.api_keys.map((k) => (
                <tr key={k.id}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                    {k.name ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <code className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs">
                      {k.prefix}…
                    </code>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {k.created_at ? formatDate(k.created_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {k.last_used_at ? formatDate(k.last_used_at) : t('table.never')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canWrite ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void rotateKey(k.id)}
                          className="mr-3 text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        >
                          {t('table.rotate')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'confirm-revoke', key: k })}
                          className="text-xs text-red-700 dark:text-red-300 hover:underline"
                        >
                          {t('table.revoke')}
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">{t('table.readOnly')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.kind === 'create' && (
        <CreateModal
          onCancel={() => setModal(null)}
          onCreate={(name) => void createKey(name)}
        />
      )}
      {modal?.kind === 'reveal' && (
        <RevealModal
          minted={modal.minted}
          rotated={modal.rotated}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'confirm-revoke' && (
        <ConfirmRevokeModal
          keyToRevoke={modal.key}
          onCancel={() => setModal(null)}
          onConfirm={() => void revokeKey(modal.key.id)}
        />
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation('mail-api-keys');
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {t('empty.title')}
      </h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('empty.lead')}</p>
      <pre className="mt-4 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
{`import { OrbotoMail } from '@orboto/mail';

const mail = new OrbotoMail({ apiKey: 'om_live_…' });

await mail.send({
  from: 'noreply@yourdomain.com',
  to: 'user@example.com',
  subject: 'Welcome',
  html: '<p>Hello!</p>',
});`}
      </pre>
      <p className="mt-4 text-xs text-gray-500">
        {t('empty.domainQuestion')}{' '}
        <Link to="/mail/domains" className="text-brand-500 hover:underline">
          {t('empty.domainLink')}
        </Link>
      </p>
    </div>
  );
}

function CreateModal({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (name: string) => void;
}) {
  const { t } = useTranslation('mail-api-keys');
  const [name, setName] = useState('');
  return (
    <ModalShell
      titleId="apikey-create-title"
      title={t('createModal.title')}
      onCancel={onCancel}
    >
      <label className="block">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{t('createModal.nameLabel')}</span>
        <span className="block text-xs text-gray-600 dark:text-gray-400">
          {t('createModal.nameHelpBefore')} <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">production</code>, <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">staging</code>.
        </span>
        <input
          type="text"
          autoFocus
          maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('createModal.namePlaceholder')}
          className="mt-2 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('createModal.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onCreate(name.trim())}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          {t('createModal.create')}
        </button>
      </div>
    </ModalShell>
  );
}

function RevealModal({
  minted,
  rotated,
  onClose,
}: {
  minted: ApiKeyMinted;
  rotated: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('mail-api-keys');
  const [copied, setCopied] = useTransientState(false, 2000);
  function copy() {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(minted.key);
      setCopied(true);
    }
  }
  return (
    <ModalShell
      titleId="apikey-reveal-title"
      title={rotated ? t('reveal.titleRotated') : t('reveal.titleCreated')}
      onCancel={onClose}
      hideCloseX
    >
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
        <strong>{t('reveal.saveNow')}</strong> {t('reveal.saveNowText')}
      </div>
      <div className="mt-4">
        <span className="block text-xs text-gray-600 dark:text-gray-400">
          {t('reveal.yourNew')} {minted.name ? <em>{minted.name}</em> : null} {t('reveal.apiKey')}
        </span>
        <div className="mt-2 flex items-stretch gap-2">
          <code
            data-testid="reveal-key-plaintext"
            className="min-w-0 flex-1 overflow-x-auto rounded bg-gray-900 px-3 py-2 text-xs text-gray-100"
          >
            {minted.key}
          </code>
          <button
            type="button"
            onClick={copy}
            className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {copied ? t('reveal.copied') : t('reveal.copy')}
          </button>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('reveal.dismiss')}
        </button>
      </div>
    </ModalShell>
  );
}

function ConfirmRevokeModal({
  keyToRevoke,
  onCancel,
  onConfirm,
}: {
  keyToRevoke: ApiKeySummary;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('mail-api-keys');
  return (
    <ModalShell
      titleId="apikey-revoke-title"
      title={t('revokeModal.title', { name: keyToRevoke.name ?? keyToRevoke.prefix })}
      onCancel={onCancel}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('revokeModal.textBefore')}{' '}
        <strong>{t('revokeModal.rotate')}</strong> {t('revokeModal.textAfter')}
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {t('revokeModal.cancel')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          {t('revokeModal.revoke')}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  titleId,
  title,
  children,
  onCancel,
  hideCloseX,
}: {
  titleId: string;
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
  hideCloseX?: boolean;
}) {
  const { t } = useTranslation('mail-api-keys');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl">
        <header className="mb-4 flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold">
            {title}
          </h2>
          {!hideCloseX && (
            <button
              type="button"
              onClick={onCancel}
              aria-label={t('close')}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60"
            >
              ✕
            </button>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso, iso);
}
